// UTM attribution on every link Mira hands a CLIENT into fiverr.com.
//
// WHY IT EXISTS. Fiverr already captures UTMs on arrival, twice over: Bianka's
// `TrafficSourceAttributed` interceptor fires on any request carrying
// `utm_source`, and the Rails `refinfo` cookie turns UTM params into a
// `referrals` row at conversion. So the ONLY thing missing for Mira to show up
// in Fiverr's own attribution is the params on the outbound link.
//
// WHERE IT RUNS. At the LAST HOP, never in stored data. A talent's
// `fiverrProfileUrl` is DATA: the backend stamps it, snapshots persist it,
// `fiverr-inbox.ts` parses it for the handle and the origin, and the handoff
// export ships it to BigQuery. Decorating THAT would poison every one of those
// readers. This module decorates where the href is RENDERED or the tab is
// NAVIGATED, and nowhere else.
//
// The backend half of this lives in `recruiter/fiverr_web.py::with_mira_utm`
// (same values, same host check, same never-overwrite rule). It covers only a
// link with NO client hop, which today is one: the static maintenance page,
// served when the SPA is not running at all. Everything else, including the
// canned notices in `attention.py`, is prose we persist and stream - and the
// renderer below uses the URL as its own link text, so a query string baked in
// upstream would be visible copy. Those get attributed here instead, on
// `agent_prose`.

/** The two constant halves of Mira's attribution. */
export const MIRA_UTM = { source: "mira", medium: "handoff" } as const;

/**
 * `utm_campaign` - the Mira SURFACE the link lives on.
 *
 * A bounded enum on purpose: these values land in Fiverr's `referrals` table
 * and in Bianka events, so the vocabulary has to stay small enough to group by.
 * NEVER encode an id here (no project, user, guest or seller id) - cross-system
 * joins are the separate `u_guid` plan's job.
 */
export type FiverrLinkSurface =
  | "results_card" // the shortlist deck's talent cards
  | "results_finalist" // the results compare table
  | "results_self_search" // the results panel's "search Fiverr myself" escape
  | "finalist_pick" // the concierge finalist-pick offer cards
  | "live_matches_rail" // the definition-phase live rail
  | "field_seller_card" // the Mira-field talent card
  | "dashboard_dead_end" // the waiting board's dead-end popup
  | "talent_engaged" // the post-pick "talent engaged" screen
  | "workspace_pick" // the workspace's pick handler (pre-opened tab)
  | "brief_push" // the take-control brief push (pre-opened tab)
  | "search_fallback" // the no-match terminal screen
  | "handoff_stage" // the concierge handoff screen
  | "user_menu" // the user menu's Help Center link
  | "handoff_scheduling" // the specialist-call booking page
  | "mcp_card" // the Mira card rendered inside Claude / ChatGPT
  | "agent_prose"; // a link Mira wrote into her own reply

/** `utm_content` - the DESTINATION kind. Bounded for the same reason. */
export type FiverrLinkContent =
  | "profile"
  | "inbox"
  | "search"
  | "home"
  | "help"
  | "meeting"
  | "support"
  | "start_selling";

/**
 * Exact-host match, never a substring: `evilfiverr.com` and
 * `fiverr.com.evil.io` must NOT be decorated, while `www.`, `www.dev.`,
 * `help.` and `propages.` must.
 */
function isFiverrHost(hostname: string): boolean {
  return hostname === "fiverr.com" || hostname.endsWith(".fiverr.com");
}

/**
 * Adds Mira's UTM params to a fiverr.com URL.
 *
 * The IDENTITY function when `url` is not an absolute URL, when the host is not
 * fiverr.com / *.fiverr.com, or when the URL already carries any `utm_*`. That
 * last rule matters: a URL that arrives already attributed belongs to whatever
 * put the attribution there, and overwriting it would move somebody else's
 * credit onto Mira. Fiverr's own system emails stamp `utm_source=system_email`,
 * so this is a real case and not a hypothetical one.
 *
 * Existing query params and the hash are preserved BYTE for byte - the original
 * string is appended to rather than re-serialized through `URLSearchParams`,
 * which would rewrite `%20` as `+` and normalize the path.
 */
export function withMiraUtm(
  url: string,
  surface: FiverrLinkSurface,
  content: FiverrLinkContent,
): string {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return url; // relative, empty, or garbage - nothing to decorate
  }
  if (!isFiverrHost(parsed.hostname)) return url;
  for (const key of parsed.searchParams.keys()) {
    if (key.toLowerCase().startsWith("utm_")) return url;
  }
  const hashAt = url.indexOf("#");
  const head = hashAt === -1 ? url : url.slice(0, hashAt);
  const hash = hashAt === -1 ? "" : url.slice(hashAt);
  // A "?" that is already there but EMPTY ("…/x?") takes no separator at all.
  const sep = head.endsWith("?") ? "" : head.includes("?") ? "&" : "?";
  const params =
    `utm_source=${MIRA_UTM.source}&utm_medium=${MIRA_UTM.medium}` +
    `&utm_campaign=${surface}&utm_content=${content}`;
  return `${head}${sep}${params}${hash}`;
}

/**
 * The `utm_content` for a link Mira WROTE into her own prose, read off the
 * destination path. Everything unrecognised is a profile: a bare
 * `fiverr.com/{handle}` is by far the commonest thing she types, and `profile`
 * is the honest default for "a page about a person".
 */
export function agentProseContent(url: string): FiverrLinkContent {
  let path: string;
  try {
    path = new URL(url).pathname.toLowerCase();
  } catch {
    return "profile";
  }
  if (path.startsWith("/inbox")) return "inbox";
  if (path.startsWith("/search")) return "search";
  if (path.startsWith("/support")) return "support";
  if (path.startsWith("/start_selling")) return "start_selling";
  return "profile";
}
